import React, { useEffect, useRef, useState } from "react";
import { Grid2, Container, Box, useTheme, useMediaQuery } from "@mui/material";
import { motion, useAnimation, useMotionValue, useTransform, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./Hero.module.css";

const Hero = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Theme Variables for CSS
    const heroStyles = {
        '--hero-bg': isLight
            ? 'radial-gradient(circle at 50% 50%, #f0f4f8 0%, #F4F6F8 100%)'
            : 'radial-gradient(circle at 50% 50%, rgba(26, 28, 102, 0.4) 0%, #0C0C0F 100%)',
        '--hero-text': isLight ? '#1a202c' : '#fff',
        '--grid-color': isLight ? 'rgba(34, 153, 183, 0.15)' : 'rgba(34, 153, 183, 0.1)',
        '--headline-color': isLight ? '#0f172a' : '#fff',
        '--subheadline-color': isLight ? '#475569' : 'rgba(255, 255, 255, 0.7)',
        '--btn-sec-bg': isLight ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.05)',
        '--btn-sec-text': isLight ? '#0f172a' : '#fff',
        '--btn-sec-border': isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
        '--btn-sec-hover-bg': isLight ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.1)',
        '--btn-sec-hover-border': isLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)',
        '--border-color': isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
        '--rocket-glow': isLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(34, 153, 183, 0.5)',
        '--hero-blend-color': isLight ? '#F4F6F8' : '#0C0C0F',
        '--hero-blend-rgb': isLight ? '244, 246, 248' : '12, 12, 15',
    };

    // Mouse Parallax state
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Smooth spring physics for parallax - Faster, more dramatic tracking
    const springConfig = { damping: 20, stiffness: 120 };
    const springX = useSpring(mouseX, springConfig);
    const springY = useSpring(mouseY, springConfig);

    // Transform mouse values to pixel movement - Reduced range on mobile to prevent clipping
    const moveRange = isMobile ? [-40, 40] : [-85, 85];
    const x = useTransform(springX, [-0.5, 0.5], moveRange);
    const y = useTransform(springY, [-0.5, 0.5], moveRange);

    const handleMouseMove = (e) => {
        // Normalize coordinates from -0.5 to 0.5
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;
        mouseX.set(clientX / innerWidth - 0.5);
        mouseY.set(clientY / innerHeight - 0.5);
    };

    useEffect(() => {
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    // Animation Controls
    const rocketControls = useAnimation();
    const glowControls = useAnimation();
    const flameControls = useAnimation();
    const flameSecondaryControls = useAnimation();
    const shockwaveControls = useAnimation();
    const rippleControls = useAnimation();
    const smokeControls = useAnimation();

    // Generate Star Data (Stable across renders)
    const starLayers = React.useMemo(() => {
        const generateStars = (count) => {
            return Array.from({ length: count }).map((_, i) => ({
                id: i,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                delay: `${Math.random() * 2}s`,
                duration: `${0.5 + Math.random() * 1.5}s`
            }));
        };
        return {
            far: generateStars(80),
            mid: generateStars(30),
            near: generateStars(8)
        };
    }, []);

    // Generate Solar Particles Data
    const solarParticles = React.useMemo(() => {
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            left: `${Math.random() * 100}%`,
            size: `${2 + Math.random() * 6}px`,
            duration: `${15 + Math.random() * 20}s`,
            delay: `-${Math.random() * 20}s`, // Start immediately at random positions
            opacity: 0.2 + Math.random() * 0.4
        }));
    }, []);

    // Parallax Transforms for Star Layers
    const starsFarX = useTransform(springX, [-0.5, 0.5], [-10, 10]);
    const starsFarY = useTransform(springY, [-0.5, 0.5], [-10, 10]);

    const starsMidX = useTransform(springX, [-0.5, 0.5], [-20, 20]);
    const starsMidY = useTransform(springY, [-0.5, 0.5], [-20, 20]);

    const starsNearX = useTransform(springX, [-0.5, 0.5], [-40, 40]);
    const starsNearY = useTransform(springY, [-0.5, 0.5], [-40, 40]);

    // Cinematic Rocket Animation Controls
    useEffect(() => {
        const sequence = async () => {
            // 1. Initial State
            rocketControls.set({ y: 100, x: 0, opacity: 0, scale: isMobile ? 0.7 : 0.9, rotate: 0 });
            glowControls.set({ opacity: 0, scale: 0.5 });
            flameControls.set({ opacity: 0, scaleY: 0.5 });
            flameSecondaryControls.set({ opacity: 0, scaleY: 0.5 });
            smokeControls.set({ opacity: 0, scale: 0.5 });
            shockwaveControls.set({ opacity: 0, scale: 0.2, strokeWidth: "4px" });
            rippleControls.set({ opacity: 0, scale: 0.2, borderWidth: "2px" });

            // 2. Entrance (Quick Rise)
            await rocketControls.start({
                y: 60,
                opacity: 1,
                scale: 1,
                transition: { duration: 0.6, ease: "easeOut" } // Faster entrance
            });

            // 3. Ignition Charge (Intense Shake & Pre-glow)
            const chargeDuration = 0.6; // Snappier charge up
            glowControls.start({
                opacity: [0, 0.5, 0.8, 1],
                scale: [1, 1.4, 2.0], // Increased scale
                transition: { duration: chargeDuration, ease: "easeIn" }
            });
            flameControls.start({
                opacity: [0, 0.4, 0.8, 1],
                scaleY: [0.5, 0.8, 0.6, 1.5], // Longer flicker
                transition: { duration: chargeDuration, times: [0, 0.3, 0.6, 1] }
            });

            // VIOLENT SHAKE during charge
            await rocketControls.start({
                x: [-3, 3, -6, 6, -10, 10, -5, 5, 0], // More violent
                y: [60, 64, 56, 64, 56, 60],
                transition: { duration: chargeDuration, ease: "linear" }
            });

            // 4. Main Ignition (Burst)
            shockwaveControls.start({
                opacity: [1, 0.8, 0],
                scale: [0.5, 5], // Massive expansion
                strokeWidth: ["15px", "0px"],
                transition: { duration: 0.4, ease: "easeOut" }
            });

            // Burst all flames to max
            flameControls.start({
                opacity: 1,
                scaleY: [1.2, 4.0, 1.5], // Even bigger burst
                scaleX: [1, 2.0, 1.0],
                transition: { duration: 0.3, ease: "easeOut" }
            });

            glowControls.start({
                opacity: [1, 0.6],
                scale: [1.5, 3.5], // Huge glow flash
                transition: { duration: 0.3 }
            });

            // Rocket "Kick" - Deep Recoil then Shoot Up
            await rocketControls.start({
                y: [60, 130, 20], // Deeper kick
                transition: { duration: 0.4, times: [0, 0.3, 1], ease: "backOut" }
            });

            // 5. Settle into Float
            await rocketControls.start({
                y: 60,
                transition: { duration: 0.8, ease: "circOut" }
            });

            // 6. Idle Orbit — Elliptical path with direction-aware banking
            // The rocket banks (tilts) in the direction it's traveling, like a real aircraft.
            // Times create asymmetric easing: quick through straight legs, slow at extremes.
            if (isMobile) {
                // Mobile: tight oval, barely any tilt, very slow
                rocketControls.start({
                    x: [0, 14, 18, 14, 0, -14, -18, -14, 0],
                    y: [60, 55, 60, 65, 60, 55, 60, 65, 60],
                    rotate: [0, 3, 0, -3, 0, 3, 0, -3, 0],
                    transition: {
                        duration: 20,
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatType: "loop",
                    }
                });
            } else {
                // Desktop: wide ellipse, the rocket banks strongly on the curves
                // Travel path: right peak → bottom arc → left peak → top arc → repeat
                // Rotate mirrors the bank angle: lean right going right, lean left going left
                rocketControls.start({
                    x: [0, 40, 85, 40, 0, -40, -85, -40, 0],
                    y: [60, 95, 110, 95, 60, 25, 10, 25, 60],
                    rotate: [0, 8, 0, -8, 0, -8, 0, 8, 0],
                    transition: {
                        duration: 22,
                        // Per-keyframe easing makes it accelerate into curves and decelerate at peaks
                        ease: [
                            [0.4, 0, 0.6, 1], // ease into first leg
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                            [0.4, 0, 0.6, 1],
                        ],
                        repeat: Infinity,
                        repeatType: "loop",
                    }
                });
            }

            // PULSING ENGINE
            const pulseTransition = { duration: 1.2, repeat: Infinity, repeatType: "mirror" };

            flameControls.start({
                opacity: [0.9, 1, 0.9],
                scaleY: [1.3, 1.8, 1.3], // Pulsing throb
                filter: ["blur(4px)", "blur(8px)", "blur(4px)"],
                transition: { duration: 0.15, repeat: Infinity, repeatType: "mirror" }
            });

            // Ambient Glow Pulse - INTENSIFIED
            glowControls.start({
                opacity: [0.7, 1.0, 0.7],
                scale: [1.5, 3.5, 1.5], // Much bigger bloom
                transition: { ...pulseTransition, duration: 2.5 } // Slower, more realistic pulse
            });

            // LOOPING RIPPLE RINGS
            rippleControls.start({
                opacity: [0, 0.7, 0], // Brighter rings
                scale: [0.2, 1.8],
                borderWidth: ["5px", "0px"],
                y: [0, 120],
                transition: {
                    duration: 1.2, // Faster rings
                    ease: "linear",
                    repeat: Infinity,
                }
            });
        };

        sequence();
    }, [rocketControls, glowControls, flameControls, flameSecondaryControls, shockwaveControls, rippleControls, smokeControls]);

    // Text Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const wordVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    };

    const buttonVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.5, ease: "backOut" }
        },
        pulse: {
            scale: [1, 1.05, 1],
            boxShadow: [
                "0 4px 20px rgba(34, 153, 183, 0.4)",
                "0 10px 30px rgba(34, 153, 183, 0.7)",
                "0 4px 20px rgba(34, 153, 183, 0.4)"
            ],
            transition: { duration: 3, repeat: Infinity, repeatType: "mirror" }
        }
    };

    return (
        <section className={styles.heroSection} style={heroStyles}>
            <div className={styles.heroGrid}></div>

            {/* --- Solar Launch Mode Background (Light Theme) --- */}
            {isLight && (
                <>
                    {/* Sun System (Right Center, sits partially behind) */}
                    <div className={styles.sunWrapper}>
                        <div className={styles.sunRays} />
                        <div className={styles.sunCore} />

                        {/* Lens Flare */}
                        <div className={styles.lensFlare}>
                            <div className={styles.lensBokeh} />
                        </div>
                    </div>

                    {/* Solar Particles / Atmospheric Motes */}
                    <div className={styles.solarParticleLayer}>
                        {solarParticles.map(p => (
                            <div
                                key={p.id}
                                className={styles.solarParticle}
                                style={{
                                    left: p.left,
                                    width: p.size,
                                    height: p.size,
                                    '--float-duration': p.duration,
                                    '--float-delay': p.delay,
                                    '--float-opacity': p.opacity,
                                    '--mote-blur': `${Math.random() * 4}px`
                                }}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* --- Star Background Layers (Dark Theme Only) --- */}
            {!isLight && (
                <>
                    {/* Layer 1: Far Stars (Static, Slow Twinkle) */}
                    <div className={`${styles.starBase} ${styles.starLayerFar}`}>
                        {starLayers.far.map(star => (
                            <div
                                key={star.id}
                                className={styles.starFar}
                                style={{
                                    top: star.top,
                                    left: star.left,
                                    '--twinkle-delay': star.delay,
                                    '--twinkle-duration': star.duration
                                }}
                            />
                        ))}
                    </div>

                    {/* Layer 2: Mid Stars (Static, Soft Glow) */}
                    <div className={`${styles.starBase} ${styles.starLayerMid}`}>
                        {starLayers.mid.map(star => (
                            <div
                                key={star.id}
                                className={styles.starMid}
                                style={{
                                    top: star.top,
                                    left: star.left,
                                    '--twinkle-delay': star.delay,
                                    '--twinkle-duration': star.duration
                                }}
                            />
                        ))}
                    </div>

                    {/* Layer 3: Near Particles (Drift only, no mouse parallax) */}
                    <div className={`${styles.starBase} ${styles.starLayerNear}`}>
                        {starLayers.near.map(star => (
                            <div
                                key={star.id}
                                className={styles.starNear}
                                style={{
                                    top: star.top,
                                    left: star.left,
                                }}
                            />
                        ))}
                    </div>
                </>
            )}


            <Container maxWidth="lg" className={styles.contentContainer}>
                <Grid2 container spacing={4} alignItems="center">
                    {/* Left Content */}
                    <Grid2 size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            style={{
                                position: "relative",
                                zIndex: 2
                            }}
                        >
                            <h1 className={styles.headline}>
                                <motion.span variants={wordVariants} style={{ display: "inline-block", marginRight: "0.25em" }}>Right</motion.span>
                                <motion.span variants={wordVariants} style={{ display: "inline-block", marginRight: "0.25em" }}>place</motion.span>
                                <motion.span variants={wordVariants} style={{ display: "inline-block", marginRight: "0.25em" }}>to</motion.span>
                                <br />
                                <motion.span
                                    variants={wordVariants}
                                    className={styles.gradientText}
                                >
                                    launch
                                </motion.span>{" "}
                                <motion.span variants={wordVariants} style={{ display: "inline-block", marginLeft: "0.25em" }}>your</motion.span>{" "}
                                <motion.span variants={wordVariants} style={{ display: "inline-block", marginLeft: "0.25em" }}>project</motion.span>
                            </h1>

                            <motion.p variants={wordVariants} className={styles.subheadline}>
                                The premier launchpad for the next generation of DeFi projects.
                                Secure, transparent, and designed for massive growth.
                            </motion.p>

                            <div className={styles.ctaGroup}>
                                <motion.button
                                    className={styles.primaryBtn}
                                    variants={buttonVariants}
                                    animate="pulse"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => navigate("/Launchpad")}
                                >
                                    Create Presale
                                </motion.button>
                                <motion.button
                                    className={styles.secondaryBtn}
                                    variants={buttonVariants}
                                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => window.open("https://trendcreator.io/", "_blank", "noopener,noreferrer")}
                                >
                                    Explore Ecosystem
                                </motion.button>
                            </div>

                        </motion.div>
                    </Grid2>

                    {/* Right Content - Rocket Animation */}
                    <Grid2 size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
                        <div className={styles.rocketContainer}>
                            {/* Outer motion.div for Mouse Parallax */}
                            <motion.div
                                style={{ x, y, zIndex: 10, position: "relative" }}
                            >
                                {/* Inner motion.div for Autonomous Rocket Logic */}
                                <motion.div animate={rocketControls}>
                                    <motion.img
                                        src="/images/rocketHeroImg.png"
                                        alt="3D Rocket Launch"
                                        className={styles.rocketImage}
                                    />

                                    {/* Light Theme: Reflective Shimmer */}
                                    {isLight && (
                                        <div className={styles.rocketShimmer} />
                                    )}

                                    {/* Layered Engine Flames */}
                                    <motion.div
                                        className={styles.flameCore}
                                        animate={flameControls}
                                        style={{
                                            filter: isLight ? "blur(3px) drop-shadow(0 0 20px rgba(255, 255, 255, 1))" : undefined,
                                            background: isLight ? "linear-gradient(180deg, #FFFFFF 10%, #FFFBEB 40%, rgba(255, 255, 255, 0.1) 100%)" : undefined
                                        }}
                                    />
                                    <motion.div
                                        className={styles.flameSecondary} /* Orange heat layer */
                                        animate={flameSecondaryControls}
                                        style={{
                                            opacity: isLight ? 0.3 : undefined,
                                            mixBlendMode: isLight ? "normal" : "screen",
                                            background: isLight ? "linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(254, 243, 199, 0.4) 60%, transparent 100%)" : undefined
                                        }}
                                    />
                                    <motion.div
                                        className={styles.flameOuter}
                                        animate={flameControls}
                                        style={{
                                            opacity: isLight ? 0.4 : undefined,
                                            background: isLight ? "linear-gradient(180deg, rgba(255, 251, 235, 0.6) 0%, rgba(255, 255, 255, 0.05) 60%, transparent 100%)" : undefined
                                        }}
                                    />
                                    <motion.div
                                        className={styles.smokeTrail}
                                        animate={smokeControls}
                                        style={{
                                            opacity: isLight ? 0.2 : undefined,
                                            background: isLight ? "radial-gradient(ellipse at top, rgba(255, 255, 255, 0.4) 0%, transparent 70%)" : undefined
                                        }}
                                    />

                                    {/* Spark Particles (Simulated Ejection) */}
                                    {[...Array(5)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            className={styles.sparkParticle}
                                            animate={{
                                                y: [0, 200 + Math.random() * 100],
                                                x: [(Math.random() - 0.5) * 50, (Math.random() - 0.5) * 150],
                                                opacity: [1, 0],
                                                scale: [1, 0],
                                            }}
                                            transition={{
                                                duration: 0.8,
                                                delay: 1.5 + Math.random() * 0.5, /* Sync with burst time approx */
                                                repeat: Infinity,
                                                repeatDelay: Math.random() * 2
                                            }}
                                            style={{
                                                background: isLight ? "#FFF" : "#FFF",
                                                boxShadow: isLight ? "0 0 10px rgba(255, 255, 255, 0.9)" : undefined
                                            }}
                                        />
                                    ))}
                                </motion.div>
                            </motion.div>

                        </div>
                    </Grid2>
                </Grid2>
            </Container>
        </section >
    );
};

export default Hero;
